import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  Plus,
  Workflow,
  Loader2,
  MoreHorizontal,
  XCircle,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useRuns, useCreateRun, useCancelRun, useDeleteRun } from '@/hooks/useIngestion'
import { useToast } from '@/hooks/use-toast'
import type { PipelineRun, PipelineRunStatus } from '@/types/ingestion'

const statusConfig: Record<PipelineRunStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }> = {
  pending: { label: 'Pending', variant: 'outline' },
  crawling: { label: 'Crawling', variant: 'default', className: 'bg-blue-600' },
  processing: { label: 'Processing', variant: 'default', className: 'bg-blue-600 animate-pulse' },
  completed: { label: 'Completed', variant: 'default', className: 'bg-green-600' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
}

function StatusBadge({ status }: { status: PipelineRunStatus }) {
  const config = statusConfig[status] ?? { label: status, variant: 'outline' as const }
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  )
}

const defaultForm = { name: '', root_url: '', depth: 2, concurrency: 3 }

export function IngestionPage() {
  const { data, isLoading, error } = useRuns()
  const createRun = useCreateRun()
  const cancelRunMut = useCancelRun()
  const deleteRunMut = useDeleteRun()
  const { toast } = useToast()

  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState(defaultForm)
  const [formError, setFormError] = useState<string | null>(null)

  const runs = data?.results ?? []

  const handleOpenModal = () => {
    setForm(defaultForm)
    setFormError(null)
    setModalOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!form.name.trim()) {
      setFormError('Run name is required.')
      return
    }
    if (!form.root_url.trim()) {
      setFormError('Root URL is required.')
      return
    }

    createRun.mutate(
      {
        name: form.name.trim(),
        root_url: form.root_url.trim(),
        depth: form.depth,
        concurrency: form.concurrency,
      },
      {
        onSuccess: () => {
          toast({ title: 'Pipeline run started' })
          setModalOpen(false)
          setForm(defaultForm)
        },
        onError: (err: any) => {
          const detail =
            err?.response?.data?.root_url?.[0] ||
            err?.response?.data?.name?.[0] ||
            err?.response?.data?.depth?.[0] ||
            err?.response?.data?.concurrency?.[0] ||
            err?.response?.data?.detail ||
            err?.message ||
            'Failed to create pipeline run'
          setFormError(String(detail))
        },
      }
    )
  }

  const handleCancel = (runUuid: string) => {
    cancelRunMut.mutate(runUuid, {
      onSuccess: () => toast({ title: 'Pipeline run cancelled' }),
      onError: () => toast({ title: 'Failed to cancel run', variant: 'destructive' }),
    })
  }

  const handleDelete = (runUuid: string) => {
    deleteRunMut.mutate(runUuid, {
      onSuccess: () => toast({ title: 'Pipeline run deleted' }),
      onError: () => toast({ title: 'Failed to delete run', variant: 'destructive' }),
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">Error loading pipeline runs</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Pipelines</h1>
          <p className="text-muted-foreground mt-2">
            Crawl websites and process pages into your knowledge base.
          </p>
        </div>
        <Button onClick={handleOpenModal}>
          <Plus className="h-4 w-4 mr-2" />
          New pipeline run
        </Button>
      </div>

      {/* Empty state */}
      {runs.length === 0 ? (
        <Card className="border-dashed border-0 shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Workflow className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No pipeline runs yet</h3>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              Create your first pipeline run to start crawling and indexing content.
            </p>
            <Button onClick={handleOpenModal}>
              <Plus className="h-4 w-4 mr-2" />
              New pipeline run
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Runs table */
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="w-[50px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run: PipelineRun) => (
                <TableRow key={run.id}>
                  <TableCell>
                    <Link
                      to={`/app/ingestion/${run.uuid}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {run.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground truncate block max-w-[220px]">
                      {run.root_url}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={run.status} />
                  </TableCell>
                  <TableCell>
                    {run.status === 'pending' || run.status === 'cancelled' ? (
                      <span className="text-muted-foreground">&mdash;</span>
                    ) : (
                      <div className="space-y-1 min-w-[120px]">
                        <span className="text-xs text-muted-foreground">
                          {run.completed_pages}/{run.total_pages} pages
                        </span>
                        <Progress value={run.progress_percentage} className="h-1.5" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {run.started_at
                        ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
                        : '\u2014'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <RunActions
                      run={run}
                      onCancel={handleCancel}
                      onDelete={handleDelete}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create run modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New pipeline run</DialogTitle>
            <DialogDescription>
              Configure and start a new website crawl pipeline.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="run-name">Run name</Label>
              <Input
                id="run-name"
                placeholder="e.g. gt_campus_rec"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="root_url">Root URL</Label>
              <Input
                id="root_url"
                type="url"
                placeholder="https://example.com/docs"
                value={form.root_url}
                onChange={(e) => setForm({ ...form, root_url: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="depth">Crawl depth</Label>
                <Input
                  id="depth"
                  type="number"
                  min={1}
                  max={5}
                  value={form.depth}
                  onChange={(e) => setForm({ ...form, depth: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">
                  How many levels deep to follow links from the root URL
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="concurrency">Concurrency</Label>
                <Input
                  id="concurrency"
                  type="number"
                  min={1}
                  max={10}
                  value={form.concurrency}
                  onChange={(e) => setForm({ ...form, concurrency: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of pages processed simultaneously
                </p>
              </div>
            </div>

            {formError && (
              <p className="text-sm text-destructive">{formError}</p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setModalOpen(false); setForm(defaultForm) }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createRun.isPending}>
                {createRun.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Start pipeline
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function RunActions({
  run,
  onCancel,
  onDelete,
}: {
  run: PipelineRun
  onCancel: (uuid: string) => void
  onDelete: (uuid: string) => void
}) {
  const canCancel = run.status === 'crawling' || run.status === 'processing'
  const canDelete = run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled'

  if (!canCancel && !canDelete) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canCancel && (
          <DropdownMenuItem onClick={() => onCancel(run.uuid)}>
            <XCircle className="h-4 w-4 mr-2" />
            Cancel run
          </DropdownMenuItem>
        )}
        {canDelete && (
          <DropdownMenuItem
            onClick={() => onDelete(run.uuid)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete run
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
